import { ShieldCheck, UserCheck, AlertTriangle, Lock } from "lucide-react";
import { aksesAkun } from "../../lib/aksesAkun";

/**
 * Section "Akun Login Portal" — dipakai di modal Employee Add/Edit.
 * Ditarik keluar dari Employees.jsx agar tidak ter-duplikat.
 *
 * Keputusan owner 18 Sep 2026: role, serta reset password/template akun
 * istimewa (role selain employee) dan akun milik sendiri, hanya bisa diubah Bos
 * (superadmin). Keputusan owner 20 Sep 2026 (§17 opsi a): akun role employee
 * yang memegang KUASA (mengelola karyawan/akun, izin, keuangan, atau
 * persetujuan) diperlakukan sama — dinilai server, bukan ditebak di sini
 * (`t.berkuasa` dari `GET /permissions/templates`, `form.login_akun_berkuasa`
 * dari `GET /employees`). Backend menolaknya (pagar_akun.py); kontrolnya di
 * sini ikut disembunyikan supaya tidak ada isian yang ujungnya 403.
 */
export default function EmployeeAccountSection({
  form, upd, isEdit, permTemplates = [], aktor,
  Field, Input, Select, LOGIN_ROLES,
}) {
  const hasAccount = !!form.has_login_account;
  const showAccountSection = !isEdit ? true : (hasAccount || form.enable_login_account);
  const suggestedPwd = (form.nik || "").slice(-6);
  const wantsAccount = !!(form.create_login_account || form.enable_login_account);
  const missingEmail = wantsAccount && !(form.email || "").trim();
  const akses = aksesAkun({ form, aktor, isEdit });
  const roleSekarang = form.login_role || "employee";
  // Role tersimpan yang tidak ada di daftar pilihan (superadmin, finance_*) tetap
  // ditampilkan apa adanya. Tanpa ini <select> menandai opsi pertama secara
  // visual — akun Bos terlihat seperti "Employee" padahal tidak.
  const opsiRole = LOGIN_ROLES.some(o => o.v === roleSekarang)
    ? LOGIN_ROLES
    : [...LOGIN_ROLES, { v: roleSekarang, l: `${roleSekarang} (role sekarang)` }];
  const labelRole = (opsiRole.find(o => o.v === roleSekarang) || {}).l || roleSekarang;
  // Template yang MENJADIKAN akun berkuasa hanya ditawarkan ke Bos — `berkuasa`
  // dihitung server (routers/users.py::list_permission_templates) dari izin
  // efektif template itu, bukan nama/daftar tetap di layar.
  const opsiTemplate = akses.isBos
    ? permTemplates
    : permTemplates.filter(t => !t.berkuasa);
  const alasanKunci = akses.akunIstimewa
    ? `Akun ini ${form.login_akun_berkuasa ? "memegang kuasa (mengelola karyawan/akun, izin, keuangan, atau persetujuan)" : `ber-role '${roleSekarang}'`}: reset password, template/izin, dan email login-nya hanya bisa diubah Bos (superadmin).`
    : "Ini akun login milikmu sendiri: template/izin hanya bisa diubah Bos (superadmin). Ganti password lewat menu Profil › Ganti Password.";

  return (
    <div>
      <h4 className="font-display font-semibold text-slate-900 border-b border-slate-100 pb-2 mb-4 flex items-center gap-2">
        <ShieldCheck className="w-4 h-4 text-emerald-600" />
        Akun Login Portal
        {isEdit && (
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${hasAccount ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>
            {hasAccount ? "Sudah punya akun" : "Belum punya akun"}
          </span>
        )}
        {isEdit && hasAccount && !!form.login_akun_berkuasa && (
          <span data-testid="badge-akun-berkuasa"
            className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">
            Berkuasa — hanya Bos yang bisa mengatur
          </span>
        )}
      </h4>
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800 mb-4">
        <UserCheck className="w-3.5 h-3.5 inline mb-0.5 mr-1" />
        {isEdit
          ? (akses.isBos
            ? "Ubah role/template atau reset password akun login karyawan ini. Email & nama akun akan tersinkron otomatis."
            : "Reset password dan template bisa diubah untuk akun karyawan biasa milik orang lain. Role akun hanya bisa diganti Bos (superadmin). Nama akun tersinkron otomatis.")
          : (akses.isBos
            ? "Akun login otomatis dibuat saat simpan. Email di section Informasi Pribadi wajib diisi. Password default = 6 digit terakhir NIK."
            : "Akun login karyawan (role employee) otomatis dibuat saat simpan. Email di section Informasi Pribadi wajib diisi. Password default = 6 digit terakhir NIK.")}
      </div>

      {missingEmail && (
        <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-lg p-3 text-xs text-amber-800 mb-4 flex items-start gap-2"
          data-testid="warning-missing-email">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Email kosong — akun login tidak akan dibuat</p>
            <p className="mt-1">Isi field <b>Email</b> di section Informasi Pribadi. Tanpa email, ceklis di bawah akan diabaikan &amp; toast error muncul saat simpan.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {!isEdit && (
          <label className="flex items-center gap-2 md:col-span-2 cursor-pointer">
            <input type="checkbox" checked={!!form.create_login_account}
              onChange={e => upd("create_login_account", e.target.checked)}
              data-testid="input-create-account"
              className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
            <span className="text-sm font-medium text-slate-700">Buatkan akun login otomatis untuk karyawan ini</span>
          </label>
        )}
        {isEdit && !hasAccount && (
          <label className="flex items-center gap-2 md:col-span-2 cursor-pointer">
            <input type="checkbox" checked={!!form.enable_login_account}
              onChange={e => upd("enable_login_account", e.target.checked)}
              data-testid="input-enable-account"
              className="w-4 h-4 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500" />
            <span className="text-sm font-medium text-slate-700">Buatkan akun login sekarang</span>
          </label>
        )}
        {showAccountSection && (
          <>
            <Field label="Role">
              {akses.bolehUbahRole ? (
                <Select value={roleSekarang}
                  onChange={v => upd("login_role", v)}
                  tid="input-login-role"
                  options={opsiRole} />
              ) : (
                <div data-testid="akun-role-terkunci"
                  className="h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-700 flex items-center gap-2">
                  <Lock className="w-3.5 h-3.5 text-slate-400" />
                  <span>{labelRole}</span>
                  <span className="ml-auto text-[10px] text-slate-400">diganti hanya oleh Bos</span>
                </div>
              )}
            </Field>
            {akses.bolehUbahTemplate && (
              <Field label="Template Hak Akses (opsional)">
                <Select value={form.login_template_key || ""}
                  onChange={v => upd("login_template_key", v)}
                  tid="input-login-template"
                  options={[
                    { v: "", l: "— pakai default role —" },
                    ...opsiTemplate.map(t => ({ v: t.key, l: `${t.is_custom ? "★ " : ""}${t.label}` })),
                  ]} />
              </Field>
            )}
            {akses.bolehResetPassword && (
              <Field label={isEdit ? "Reset Password (kosongkan bila tak berubah)" : `Password (default: ${suggestedPwd || "6 digit NIK"})`}>
                <Input type="text"
                  value={isEdit ? (form.login_password_reset || "") : (form.login_password || "")}
                  onChange={v => upd(isEdit ? "login_password_reset" : "login_password", v)}
                  tid="input-login-password" />
              </Field>
            )}
            {!(akses.bolehUbahTemplate && akses.bolehResetPassword) && (
              <div data-testid="akun-terkunci-info"
                className="md:col-span-2 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 flex items-start gap-2">
                <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                <span>{alasanKunci}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
